import dataService from "../../api";
import { startLoading, stopLoading, serverErrorMessage, showNotification, showAlert, catchHandler } from "../../utils/helpers";

const state = {
    devices: [],
    device: {},
    syncRuns: [],
    mappings: [],
    unmapped: [],
    punches: [],
    totalItems: 0,
};

const getters = {
    index: state => deviceId => state.devices.findIndex(i => i.deviceId === deviceId),
    mappingIndex: state => id => state.mappings.findIndex(i => i.employeeDeviceUserId === id),
};

const mutations = {
    setDevices: (state, r) => (state.devices = r),
    setDevice: (state, r) => (state.device = r),
    setSyncRuns: (state, r) => (state.syncRuns = r),
    setMappings: (state, r) => (state.mappings = r),
    setUnmapped: (state, r) => (state.unmapped = r),
    setPunches: (state, r) => (state.punches = r),
    setTotalItems: (state, r) => (state.totalItems = r),
    delete: (state, index) => state.devices.splice(index, 1),
    deleteMapping: (state, index) => state.mappings.splice(index, 1),
};

const buildParams = params =>
    Object.entries(params)
        .filter(([, val]) => val !== null && val !== undefined && val !== "")
        .map(([key, val]) => `${key}=${val}`)
        .join("&");

const actions = {
    async getAll({ commit }) {
        try {
            startLoading()
            const response = await dataService.attendanceDevices.getAll();
            stopLoading()

            if (!response.data.statusCode) {
                showAlert(serverErrorMessage);
                return;
            }

            commit("setDevices", response.data.devices);
        } catch (error) {
            stopLoading()
            catchHandler(error);
        }
    },
    async add(_, payload) {
        try {
            startLoading()
            const response = await dataService.attendanceDevices.add(payload);
            stopLoading()

            if (!response.data.statusCode) {
                showAlert(serverErrorMessage);
                return;
            }

            showNotification(response.data.message);
            return Promise.resolve();
        } catch (error) {
            stopLoading()
            catchHandler(error);
            return Promise.reject(error);
        }
    },
    async getForEdit(_, id) {
        try {
            startLoading()
            const response = await dataService.attendanceDevices.getForEdit(id);
            stopLoading()

            if (!response.data.statusCode) {
                showAlert(serverErrorMessage);
                return;
            }

            return Promise.resolve(response.data.device);
        } catch (error) {
            stopLoading()
            catchHandler(error);
        }
    },
    async edit(_, { id, payload }) {
        try {
            startLoading()
            const response = await dataService.attendanceDevices.edit(id, payload);
            stopLoading()

            if (!response.data.statusCode) {
                showAlert(serverErrorMessage);
                return;
            }

            showNotification(response.data.message);
            return Promise.resolve();
        } catch (error) {
            stopLoading()
            catchHandler(error);
            return Promise.reject(error);
        }
    },
    async delete({ commit, getters }, id) {
        try {
            startLoading()
            const response = await dataService.attendanceDevices.delete(id);
            stopLoading()

            if (!response.data.statusCode) {
                showAlert(serverErrorMessage);
                return;
            }

            showNotification(response.data.message);

            const index = getters.index(id);
            commit("delete", index);
        } catch (error) {
            stopLoading()
            catchHandler(error);
        }
    },
    async getSyncRuns({ commit }, id) {
        try {
            commit("setSyncRuns", []);
            startLoading()
            const response = await dataService.attendanceDevices.getSyncRuns(id);
            stopLoading()

            if (!response.data.statusCode) {
                showAlert(serverErrorMessage);
                return;
            }

            commit("setSyncRuns", response.data.runs);
        } catch (error) {
            stopLoading()
            catchHandler(error);
        }
    },

    // ── Employee ↔ device user mapping ──────────────────────────

    async getMappings({ commit }, params) {
        try {
            startLoading()
            const response = await dataService.attendanceDevices.getMappings(buildParams(params || {}));
            stopLoading()

            if (!response.data.statusCode) {
                showAlert(serverErrorMessage);
                return;
            }

            commit("setMappings", response.data.mappings);
        } catch (error) {
            stopLoading()
            catchHandler(error);
        }
    },
    async getUnmapped({ commit }) {
        try {
            startLoading()
            const response = await dataService.attendanceDevices.getUnmapped();
            stopLoading()

            if (!response.data.statusCode) {
                showAlert(serverErrorMessage);
                return;
            }

            commit("setUnmapped", response.data.unmapped);
        } catch (error) {
            stopLoading()
            catchHandler(error);
        }
    },
    async addMapping(_, payload) {
        try {
            startLoading()
            const response = await dataService.attendanceDevices.addMapping(payload);
            stopLoading()

            if (!response.data.statusCode) {
                showAlert(serverErrorMessage);
                return;
            }

            showNotification(response.data.message);
            return Promise.resolve();
        } catch (error) {
            stopLoading()
            catchHandler(error);
            return Promise.reject(error);
        }
    },
    async deleteMapping({ commit, getters }, id) {
        try {
            startLoading()
            const response = await dataService.attendanceDevices.deleteMapping(id);
            stopLoading()

            if (!response.data.statusCode) {
                showAlert(serverErrorMessage);
                return;
            }

            showNotification(response.data.message);

            const index = getters.mappingIndex(id);
            commit("deleteMapping", index);
        } catch (error) {
            stopLoading()
            catchHandler(error);
        }
    },

    // ── Raw punches ─────────────────────────────────────────────

    async getPunches({ commit }, params) {
        try {
            startLoading()
            const response = await dataService.attendanceDevices.getPunches(buildParams(params));
            stopLoading()

            if (!response.data.statusCode) {
                showAlert(serverErrorMessage);
                return;
            }

            commit("setPunches", response.data.punches);
            commit("setTotalItems", response.data.totalItems);
        } catch (error) {
            stopLoading()
            catchHandler(error);
        }
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
};
